import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui';
import LanguageSwitcher from '../components/layout/LanguageSwitcher';
import logo from '../assests/ampera_logo.png';
import gsap from 'gsap';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Format currency
const formatCurrency = (amount, lang) => {
  if (!amount) return 'Rp 0';
  return new Intl.NumberFormat(lang === 'id' ? 'id-ID' : 'en-US', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
};

// Format date
const formatDate = (dateStr, lang) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function Account() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { profile, isAuthenticated, loading, isAdmin, isMedia, isInvestor, isCustomer, signOut, refreshProfile } = useAuth();
  const lang = i18n.language;

  // Avatar error state
  const [avatarError, setAvatarError] = useState(false);

  // Resolve last_visited_branch (stored as a branch_id UUID) to a readable name.
  const [branchName, setBranchName] = useState(null);
  useEffect(() => {
    const raw = profile?.last_visited_branch;
    if (!raw) { setBranchName(null); return; }
    if (!UUID_RE.test(raw)) { setBranchName(raw); return; } // already a name
    let cancelled = false;
    supabase
      .from('branches')
      .select('name')
      .eq('id', raw)
      .single()
      .then(({ data }) => { if (!cancelled) setBranchName(data?.name || null); });
    return () => { cancelled = true; };
  }, [profile?.last_visited_branch]);

  // Refs for animations
  const headerRef = useRef(null);
  const sidebarRef = useRef(null);
  const mainRef = useRef(null);
  const statsRef = useRef(null);
  const cardsRef = useRef(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Reset avatar error when profile changes
  useEffect(() => {
    setAvatarError(false);
  }, [profile?.avatar_url]);

  // GSAP Animations
  useEffect(() => {
    if (!loading && profile) {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Header animation
      if (headerRef.current) {
        tl.fromTo(
          headerRef.current,
          { opacity: 0, y: -30 },
          { opacity: 1, y: 0, duration: 0.6 }
        );
      }

      // Sidebar animation
      if (sidebarRef.current) {
        tl.fromTo(
          sidebarRef.current,
          { opacity: 0, x: -50 },
          { opacity: 1, x: 0, duration: 0.8 },
          '-=0.3'
        );
      }

      // Main content animation
      if (mainRef.current) {
        tl.fromTo(
          mainRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.6 },
          '-=0.5'
        );
      }

      // Stats animation
      if (statsRef.current) {
        tl.fromTo(
          statsRef.current.children,
          { opacity: 0, y: 20, scale: 0.9 },
          { opacity: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.1 },
          '-=0.3'
        );
      }

      // Cards animation
      if (cardsRef.current) {
        tl.fromTo(
          cardsRef.current.children,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.15 },
          '-=0.2'
        );
      }
    }
  }, [loading, profile]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Get portal link based on role
  const getPortalInfo = () => {
    if (isAdmin) return { link: '/admin', label: t('auth.adminPanel'), icon: '🛡️' };
    if (isMedia) return { link: '/admin', label: t('auth.mediaPanel'), icon: '📰' };
    if (isInvestor) return { link: '/investor', label: t('auth.investorPortal'), icon: '📈' };
    if (isCustomer) return { link: '/portal', label: t('auth.customerPortal'), icon: '🎁' };
    return null;
  };

  // Get tier info
  const getTierInfo = (tier) => {
    const tiers = {
      'Regular': { color: 'bg-gray-100 text-gray-700', icon: '⭐' },
      'Silver': { color: 'bg-gray-200 text-gray-800', icon: '🥈' },
      'Gold': { color: 'bg-yellow-100 text-yellow-800', icon: '🥇' },
      'Platinum': { color: 'bg-purple-100 text-purple-800', icon: '💎' },
      'Diamond': { color: 'bg-cyan-100 text-cyan-800', icon: '💠' },
    };
    return tiers[tier] || tiers['Regular'];
  };

  const portalInfo = getPortalInfo();
  const tierInfo = getTierInfo(profile?.tier);

  // Navigation links
  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/menu', label: t('nav.menu') },
    { to: '/branches', label: t('nav.branches') },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <span className="text-gray-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  // Authenticated but no profile row yet — show a friendly recovery state
  // instead of a blank page.
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="text-5xl mb-4" aria-hidden="true">👋</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('account.noProfileTitle', 'Complete your profile')}</h1>
          <p className="text-gray-600 mb-6">
            {t('account.noProfileBody', "We couldn't find your profile details. Try again, or contact support if this keeps happening.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => refreshProfile()}>{t('common.retry', 'Try again')}</Button>
            <Button variant="outline" to="/contact">{t('account.contactSupport')}</Button>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-6 text-sm text-gray-500 hover:text-gray-700"
          >
            {t('auth.signOut')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50">
      {/* Header */}
      <header ref={headerRef} className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Ampera" className="h-10 w-auto" />
            </Link>

            {/* Nav Links - Desktop */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-4 py-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors text-sm font-medium"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
              >
                {t('auth.signOut')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Profile Card */}
          <aside ref={sidebarRef} className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-24">
              {/* Profile Header */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-6 text-center relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

                <div className="relative">
                  {/* Avatar */}
                  <div className="w-24 h-24 rounded-full bg-white/20 mx-auto mb-4 overflow-hidden ring-4 ring-white/30">
                    {profile.avatar_url && !avatarError ? (
                      <img
                        src={profile.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-white/20">
                        <span className="text-4xl font-bold text-white">
                          {profile.full_name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Name & Username */}
                  <h1 className="text-xl font-bold text-white mb-1">
                    {profile.full_name || t('account.noName')}
                  </h1>
                  {profile.username && (
                    <p className="text-white/70 text-sm">@{profile.username}</p>
                  )}

                  {/* Badges */}
                  <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                    <span className={`px-3 py-1 text-sm rounded-full font-medium ${tierInfo.color}`}>
                      {tierInfo.icon} {profile.tier || 'Regular'}
                    </span>
                    {profile.is_employee && (
                      <span className="px-3 py-1 bg-yellow-400 text-yellow-900 text-sm rounded-full font-medium">
                        {t('account.employee')}
                      </span>
                    )}
                    {profile.student && (
                      <span className="px-3 py-1 bg-blue-400 text-blue-900 text-sm rounded-full font-medium">
                        {t('account.student')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="p-4 space-y-2">
                {portalInfo && (
                  <Link
                    to={portalInfo.link}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 transition-colors font-medium"
                  >
                    <span className="text-xl">{portalInfo.icon}</span>
                    {portalInfo.label}
                    <svg className="w-5 h-5 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                )}
                <Link
                  to="/branches"
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-medium"
                >
                  <span className="text-xl">📍</span>
                  {t('account.findBranch')}
                  <svg className="w-5 h-5 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {/* Member Since */}
              <div className="px-4 pb-4">
                <div className="text-center text-sm text-gray-500 py-3 border-t border-gray-100">
                  {t('account.memberSince')}: {formatDate(profile.with_ampera || profile.created_at, lang)}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main ref={mainRef} className="flex-1 space-y-6">
            {/* Stats Grid */}
            <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Loyalty Points */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">⭐</span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {(profile.loyalty_points || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500 mt-1">{t('account.loyaltyPoints')}</div>
              </div>

              {/* Raffle Tickets */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🎟️</span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {(profile.raffle_tickets || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500 mt-1">{t('account.raffleTickets')}</div>
              </div>

              {/* Total Visits */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🏪</span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {(profile.number_of_visits || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500 mt-1">{t('account.totalVisits')}</div>
              </div>

              {/* Lifetime Spent */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(profile.lifetime_spent, lang)}
                </div>
                <div className="text-sm text-gray-500 mt-1">{t('account.lifetimeSpent')}</div>
              </div>
            </div>

            {/* Info Cards */}
            <div ref={cardsRef} className="grid md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-sm">📱</span>
                  {t('account.contactInfo')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500">{t('account.phone')}</p>
                      <p className="font-medium text-gray-900 truncate">{profile.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500">{t('account.email')}</p>
                      <p className="font-medium text-gray-900 truncate">{profile.email || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-sm">👤</span>
                  {t('account.personalInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {profile.birthday && (
                    <div>
                      <p className="text-sm text-gray-500">{t('account.birthday')}</p>
                      <p className="font-medium text-gray-900">{formatDate(profile.birthday, lang)}</p>
                    </div>
                  )}
                  {profile.gender && (
                    <div>
                      <p className="text-sm text-gray-500">{t('account.gender')}</p>
                      <p className="font-medium text-gray-900 capitalize">
                        {profile.gender === 'male' ? t('account.male') : profile.gender === 'female' ? t('account.female') : profile.gender}
                      </p>
                    </div>
                  )}
                  {profile.from_what_city && (
                    <div>
                      <p className="text-sm text-gray-500">{t('account.city')}</p>
                      <p className="font-medium text-gray-900">{profile.from_what_city}</p>
                    </div>
                  )}
                  {!profile.birthday && !profile.gender && !profile.from_what_city && (
                    <div className="col-span-2 text-gray-400 text-sm py-4 text-center">
                      {t('account.notSet')}
                    </div>
                  )}
                </div>
              </div>

              {/* Activity Section */}
              {branchName && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-sm">📍</span>
                    {t('account.activity')}
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('account.lastVisited')}</p>
                      <p className="font-medium text-gray-900">{branchName}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Student Discount */}
              {profile.student && profile.student_expire && (
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl">🎓</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{t('account.studentDiscount')}</h3>
                      <p className="text-blue-100 text-sm mt-1">
                        {t('account.validUntil')}: {formatDate(profile.student_expire, lang)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Section */}
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <p className="text-gray-600 mb-2">{t('account.needHelp')}</p>
              <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">
                {t('account.contactSupport')} →
              </Link>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
